import React, { useMemo } from 'react'
import { CircularProgress, makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import clsx from 'clsx'
import { get } from 'lodash'
import Text from '../../../../atoms/Text'
import Icon from '../../../../atoms/Icon'
import { useWealthJourneyEntryImage } from '../../../../../api/wealthJourney'

const useCardStyles = makeStyles((theme) => ({
  card: {
    userSelect: 'none',
    opacity: ({ disabled }) => (disabled ? 0.5 : 1),
    position: 'relative',
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: '1rem',
    width: 'auto',
    maxWidth: '230px',
    minWidth: '210px',
    height: '280px',
    borderRadius: 'var(--border-radius-md)',
    padding: '1.625rem 1rem 1rem 1rem',
    backgroundColor: 'var(--color-surface-050)',
    boxShadow: theme.layout.shadow.card,
    boxSizing: 'border-box',
    textAlign: 'center',
    transition: 'box-shadow .2s ease-in-out, outline .2s ease-in-out',
    outline: '2px solid transparent',
    cursor: ({ clickable }) => (clickable ? 'pointer' : 'default'),
    '&:hover': {
      outlineColor: ({ disabled, clickable }) =>
        disabled ? 'var(--color-surface-100)' : (clickable ? 'color-mix(in srgb, var(--color-primary-500) 50%, transparent)' : 'transparent'),
      cursor: ({ disabled, clickable }) => (disabled ? 'initial' : (clickable ? 'pointer' : 'default'))
    },
    '&:active': {
      backgroundColor: ({ clickable }) => clickable ? 'color-mix(in srgb, var(--color-primary-500) 15%, transparent)' : 'var(--color-surface-050)'
    },
    '&.__selected': {
      outlineColor: 'var(--color-primary-500)',
      boxShadow: theme.layout.shadow.deep
    }
  },
  horizontalCard: {
    userSelect: 'none',
    opacity: ({ disabled }) => (disabled ? 0.5 : 1),
    position: 'relative',
    display: 'flex',
    flexFlow: 'row nowrap',
    gap: theme.layout.gap.g10,
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: '1rem',
    width: 'auto',
    minHeight: '120px',
    borderRadius: 'var(--border-radius-md)',
    padding: theme.layout.padding.p10,
    backgroundColor: 'var(--color-surface-050)',
    boxShadow: theme.layout.shadow.card,
    boxSizing: 'border-box',
    transition: 'box-shadow .2s ease-in-out, outline .2s ease-in-out',
    outline: '2px solid transparent',
    cursor: ({ clickable }) => (clickable ? 'pointer' : 'default'),
    '&:hover': {
      outlineColor: ({ disabled, clickable }) =>
        disabled ? 'var(--color-surface-100)' : (clickable ? 'color-mix(in srgb, var(--color-primary-500) 50%, transparent)' : 'transparent'),
      cursor: ({ disabled, clickable }) => (disabled ? 'initial' : (clickable ? 'pointer' : 'default'))
    },
    '&:active': {
      backgroundColor: ({ clickable }) => clickable ? 'color-mix(in srgb, var(--color-primary-500) 15%, transparent)' : 'var(--color-surface-050)'
    },
    '&.__selected': {
      outlineColor: 'var(--color-primary-500)',
      boxShadow: theme.layout.shadow.deep
    }
  },
  horizontalCompact: {
    userSelect: 'none',
    opacity: ({ disabled }) => (disabled ? 0.5 : 1),
    display: 'flex',
    flexFlow: 'row nowrap',
    gap: theme.layout.gap.g10,
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: '0.875rem',
    padding: theme.layout.padding.p5,
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'var(--color-surface-050)',
    transition: 'background-color .2s ease-in-out',
    cursor: ({ clickable }) => (clickable ? 'pointer' : 'default'),
    '&:hover': {
      backgroundColor: ({ clickable }) => clickable ? 'color-mix(in srgb, var(--color-primary-500) 10%, transparent)' : 'var(--color-surface-050)',
      cursor: ({ clickable }) => (clickable ? 'pointer' : 'default')
    },
    '&.__selected': {
      backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 20%, transparent)'
    }
  },
  title: {
    fontSize: '1rem',
    fontWeight: 700,
    textAlign: ({ variant }) => variant === 'card' ? 'center' : 'left',
    paddingTop: ({ variant }) => variant === 'card' ? '1.25rem' : '0',
    overflow: 'hidden',
    whiteSpace: ({ variant }) => variant === 'horizontal-card' ? 'nowrap' : 'normal',
    textOverflow: ({ variant }) => variant === 'horizontal-card' ? 'ellipsis' : 'clip',
    width: '100%'
  },
  target: {
    fontSize: '0.75rem',
    opacity: '0.5',
    textAlign: ({ variant }) => variant === 'card' ? 'center' : 'left',
    position: ({ variant }) => variant === 'card' ? 'absolute' : 'relative',
    bottom: ({ variant }) => variant === 'card' ? '1rem' : 'auto'
  },
  avatar: {
    width: ({ variant }) => variant === 'horizontal-compact' ? '2rem' : '5.5rem',
    height: ({ variant }) => variant === 'horizontal-compact' ? '2rem' : '5.5rem',
    fontSize: ({ variant }) => variant === 'horizontal-compact' ? '1rem' : '2rem',
    marginTop: ({ variant }) => variant === 'card' ? '0.5rem' : '0',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    border: 'var(--border-neutral-thin)'
  },
  icon: {
    backgroundColor: ({ iconBackgroundColor }) => iconBackgroundColor || 'var(--color-surface-100)',
    color: ({ iconColor }) => iconColor || 'var(--color-primary-500)'
  },
  image: {
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center center'
  },
  rightSection: {
    flex: '1 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  typeMarker: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: ({ markerColor }) => markerColor || 'var(--color-primary-500)'
  }
}))

const DDPWealthJourneyEntryListItem = ({
  entry,
  variant,
  disabled,
  selected,
  onClick,
  targetPrefix,
  imageAccessor,
  imageRequestType,
  iconAccessor,
  iconColorAccessor,
  iconBackgroundColorAccessor,
  titleAccessor,
  dateAccessor,
  colorAccessor,
  className,
  style
}) => {
  // Get dynamic values via accessors
  const iconName = useMemo(() => iconAccessor ? get(entry, iconAccessor) : entry.entryType?.defaultIcon, [entry, iconAccessor])
  const iconColor = useMemo(() => iconColorAccessor ? get(entry, iconColorAccessor) : null, [entry, iconColorAccessor])
  const iconBackgroundColor = useMemo(() => iconBackgroundColorAccessor ? get(entry, iconBackgroundColorAccessor) : null, [entry, iconBackgroundColorAccessor])
  const markerColor = useMemo(() => colorAccessor ? get(entry, colorAccessor) : entry.entryType?.color, [entry, colorAccessor])

  const imageId = useMemo(() => {
    return imageAccessor ? get(entry, imageAccessor) : null
  }, [entry, imageAccessor])

  // Fetch entry image using React Query
  const shouldFetchImage = useMemo(() => imageRequestType === 'wj-get-entry-image' && !!imageId, [imageRequestType, imageId])
  const { data: imageData, isLoading: isImageLoading } = useWealthJourneyEntryImage(
    entry.entryId,
    imageId,
    { enabled: shouldFetchImage, select: (data) => data?.downloadUrl || null }
  )

  const classes = useCardStyles({
    disabled,
    variant,
    clickable: Boolean(onClick),
    iconColor,
    iconBackgroundColor,
    markerColor
  })

  const targetDate = useMemo(() => {
    const dateValue = dateAccessor ? get(entry, dateAccessor) : entry.entryDate
    const entryDate = dayjs.utc(dateValue)
    return entryDate.format('MMM YYYY')
  }, [entry, dateAccessor])

  const title = useMemo(() => {
    if (titleAccessor) {
      return get(entry, titleAccessor) || 'Untitled'
    }
    return entry.entryJson?.title || entry.entryText || 'Untitled'
  }, [entry, titleAccessor])

  const handleClick = (e) => {
    if (onClick && !disabled) {
      onClick(entry, e)
    }
  }

  const avatarContent = (
    <div className={classes.avatar}>
      {shouldFetchImage && isImageLoading ? (
        <CircularProgress />
      ) : imageData ? (
        <div
          alt={title}
          style={{ backgroundImage: `url(${imageData})` }}
          className={classes.image}
        />
      ) : iconName ? (
        <Icon name={iconName} customSize='2rem' />
      ) : (entry.entryType?.entryTypeCode?.charAt(0).toUpperCase() || 'E')}
    </div>
  )

  // Card variant (vertical)
  if (variant === 'card') {
    return (
      <div
        className={clsx(classes.card, { __selected: selected }, 'wj_entry', className)}
        style={style}
        data-entry-id={entry.entryId}
        data-year={entry.year}
        onClick={handleClick}
      >
        <Text
          className={classes.milestoneHeader}
          text={entry?.client?.longName || ''}
        />
        {avatarContent}
        <div className={classes.title}>
          {title}
        </div>
        <div className={classes.target}>
          {targetPrefix} {targetDate}
        </div>
      </div>
    )
  }

  // Horizontal card variant
  if (variant === 'horizontal-card') {
    return (
      <div
        className={clsx(classes.horizontalCard, { __selected: selected }, 'wj_entry', className)}
        style={style}
        data-entry-id={entry.entryId}
        data-year={entry.year}
        onClick={handleClick}
      >
        {avatarContent}
        <div className={classes.rightSection}>
          <div className={classes.title}>
            {title}
          </div>
          <div className={classes.target}>
            {targetPrefix} {targetDate}
          </div>
        </div>
      </div>
    )
  }

  // Horizontal compact variant
  if (variant === 'horizontal-compact') {
    return (
      <div
        className={clsx(classes.horizontalCompact, { __selected: selected }, 'wj_entry', className)}
        style={style}
        data-entry-id={entry.entryId}
        data-year={entry.year}
        onClick={handleClick}
      >
        <div className={classes.typeMarker} />
        <div className={classes.rightSection}>
          <div className={classes.title}>
            {title}
          </div>
        </div>
      </div>
    )
  }

  return null
}

DDPWealthJourneyEntryListItem.propTypes = {
  /** Entry data object */
  entry: PropTypes.shape({
    entryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    entryDate: PropTypes.string.isRequired,
    entryText: PropTypes.string,
    entryJson: PropTypes.object,
    entryType: PropTypes.object,
    client: PropTypes.object,
    year: PropTypes.number
  }).isRequired,

  /** Display variant */
  variant: PropTypes.oneOf(['card', 'horizontal-card', 'horizontal-compact']),

  /** Whether entry is disabled */
  disabled: PropTypes.bool,

  /** Whether entry is selected */
  selected: PropTypes.bool,

  /** Click handler - receives (entry, event) */
  onClick: PropTypes.func,

  /** Target date prefix text */
  targetPrefix: PropTypes.string,

  /** Accessor path for image ID (e.g., 'entryJson.imageId') */
  imageAccessor: PropTypes.string,

  /** Type of image request - currently supports 'wj-get-entry-image' */
  imageRequestType: PropTypes.string,

  /** Accessor path for icon name (e.g., 'entryType.defaultIcon') */
  iconAccessor: PropTypes.string,

  /** Accessor path for icon color */
  iconColorAccessor: PropTypes.string,

  /** Accessor path for icon background color */
  iconBackgroundColorAccessor: PropTypes.string,

  /** Accessor path for title text */
  titleAccessor: PropTypes.string,

  /** Accessor path for date value */
  dateAccessor: PropTypes.string,

  /** Accessor path for color/marker color (for compact variant) */
  colorAccessor: PropTypes.string,

  /** Additional CSS class */
  className: PropTypes.string,

  /** Inline styles */
  style: PropTypes.object
}

DDPWealthJourneyEntryListItem.defaultProps = {
  variant: 'card',
  selected: false,
  disabled: false,
  onClick: null,
  targetPrefix: 'Target:',
  iconAccessor: null,
  imageAccessor: null,
  imageRequestType: null,
  iconColorAccessor: null,
  iconBackgroundColorAccessor: null,
  titleAccessor: null,
  dateAccessor: null,
  colorAccessor: null,
  className: '',
  style: {}
}

export default DDPWealthJourneyEntryListItem
